import { useId, useState } from "react";
import { Aviso, Boton, Campo, Modal } from "../../components/ui/primitivos";
import { MOTIVO_MAX, MOTIVO_MIN, motivoListo } from "./acciones";
import { CampoArchivoFiscal } from "./CampoArchivoFiscal";

/**
 * Revertir una facturación (ADR 0099 A2, B2): sin la nota de crédito —archivo y
 * número— y un motivo de 10 a 500 caracteres no se anula. El botón queda
 * deshabilitado hasta tener las tres cosas, y un rechazo del worker se muestra
 * adentro sin cerrar ni perder lo cargado.
 */
export function DialogoRevertir({
  abierto,
  numero,
  facturaRef,
  onCerrar,
  onConfirmar,
}: {
  abierto: boolean;
  numero: string;
  facturaRef: string | null;
  onCerrar: () => void;
  onConfirmar: (datos: {
    notaCredito: File;
    notaCreditoRef: string;
    motivo: string;
  }) => Promise<void>;
}) {
  const [notaCredito, setNotaCredito] = useState<File | null>(null);
  const [notaCreditoRef, setNotaCreditoRef] = useState("");
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const idMotivo = useId();
  const idContador = useId();
  const largo = [...motivo.trim()].length;
  const listo = notaCredito !== null && notaCreditoRef.trim().length > 0 && motivoListo(motivo);

  function cerrar() {
    setNotaCredito(null);
    setNotaCreditoRef("");
    setMotivo("");
    setError(null);
    onCerrar();
  }

  return (
    <Modal
      abierto={abierto}
      onCerrar={cerrar}
      titulo={`Revertir facturación de ${numero}`}
      descripcion={`Se anula la factura ${facturaRef ?? ""} con su nota de crédito. La OT no cambia de estado.`}
    >
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!notaCredito || !listo) return;
          setEnviando(true);
          setError(null);
          try {
            await onConfirmar({
              notaCredito,
              notaCreditoRef: notaCreditoRef.trim(),
              motivo: motivo.trim(),
            });
            cerrar();
          } catch (err) {
            setError(err instanceof Error ? err.message : "No se pudo revertir");
          } finally {
            setEnviando(false);
          }
        }}
      >
        <CampoArchivoFiscal
          etiqueta="Nota de crédito"
          name="notaCredito"
          alElegir={(archivo) => {
            setNotaCredito(archivo);
            if (archivo) setError(null);
          }}
          alRechazar={setError}
        />
        <Campo
          etiqueta="Número de nota de crédito"
          name="notaCreditoRef"
          value={notaCreditoRef}
          onChange={(e) => setNotaCreditoRef(e.target.value)}
          required
        />
        <div className="space-y-1">
          <label htmlFor={idMotivo} className="block text-neutral-600 text-sm">
            Motivo
          </label>
          <textarea
            id={idMotivo}
            rows={3}
            value={motivo}
            aria-describedby={idContador}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
          />
          <p
            id={idContador}
            className={
              largo > MOTIVO_MAX || (largo > 0 && largo < MOTIVO_MIN)
                ? "text-red-700 text-xs"
                : "text-neutral-500 text-xs"
            }
          >
            {largo}/{MOTIVO_MAX} · mínimo {MOTIVO_MIN} caracteres
          </p>
        </div>

        {error ? <Aviso>{error}</Aviso> : null}

        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={cerrar}>
            Volver
          </Boton>
          <Boton type="submit" variante="peligro" disabled={!listo || enviando}>
            {enviando ? "Revirtiendo…" : "Revertir facturación"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}
